<template>
  <div class="inviteCsvMappingForm">
    <p class="mappingIntro">
      For each invite field our system expects, choose the column from your uploaded CSV that provides it. Fields you
      leave unmapped are left blank. This lets you load a CSV in any column layout — the mapping applies to this upload
      only.
    </p>

    <p v-if="headers.length === 0" class="alertBox">
      No columns were read from the uploaded CSV. Re-select a file with a header row.
    </p>

    <template v-else>
      <div class="mappingGrid">
        <div v-for="(columnSet, setIndex) in columnSets" :key="setIndex" class="mappingColumn">
          <div v-for="col in columnSet"
               :key="col.header"
               class="mappingRow"
               :class="highlightClass(col)">
            <label class="mappingFieldLabel" :for="'map_' + col.key">
              {{ col.label }}<span v-if="col.required" class="req" aria-hidden="true">*</span>
            </label>
            <vue-multiselect :id="'map_' + col.key"
                             v-model="selections[col.header]"
                             :options="availableHeaders(col)"
                             track-by="key"
                             label="original"
                             :show-labels="false"
                             :allow-empty="true"
                             placeholder="— not mapped —" />
          </div>
        </div>
      </div>

      <p v-if="validationAttempted && missingRequired.length > 0" class="error mappingSummary">
        Map every required field before the CSV can be loaded: {{ missingRequired.map(c => c.label).join(', ') }}.
      </p>
      <p v-else-if="validationAttempted && missingOptional.length > 0" class="mappingSummary mappingSummaryWarning">
        Optional fields highlighted in yellow are not mapped and will be left blank.
      </p>

      <div class="mappingActions">
        <button type="button" @click="validate">Validate Mappings</button>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import * as Vue from 'vue';
import { VisibleInviteColumns, InviteColumn } from '@/constants/inviteColumns';
import { CsvHeader, InviteColumnMapping } from '@/types/adminInvites';

// MM-42243: the inline column-mapping subform under the upload's "Mappings" section. Lists the invite fields our
// system expects (the operator-facing canonical columns) and, for each, a dropdown of the client's own CSV columns,
// pre-selecting any column whose name already matches. Each CSV column can feed only one field: once selected it drops
// out of every other field's dropdown, and returns when deselected. "Validate Mappings" highlights every unmapped field — red when
// the field is required (blocks the load), yellow when optional (left blank) — and, when no required field is
// missing, emits the map (our canonical HEADER -> the chosen client column's camelCase key) for the parent to re-parse
// the upload with. Highlights track later selection changes once a validation has been attempted, and reset when a new
// file's headers arrive. The fields lay out in two columns that stack on narrow viewports. Nothing is persisted; the
// mapping lives only for this upload.
export default Vue.defineComponent({
  name: 'InviteCsvMappingForm',
  props: {
    // The client CSV's columns (original header text + DataWeaver camelCase key) from the :headers endpoint.
    headers: { type: Array as Vue.PropType<CsvHeader[]>, default: () => [] }
  },
  emits: {
    validated: (_mapping: InviteColumnMapping) => true
  },
  data() {
    return {
      // Keyed by our canonical header; value is the selected client CsvHeader (or null when unmapped).
      selections: {} as Record<string, CsvHeader | null>,
      validationAttempted: false as boolean
    };
  },
  computed: {
    columns(): InviteColumn[] {
      return VisibleInviteColumns;
    },
    // The fields split into two halves, in display order: the first half fills the left column and the rest the
    // right, so when the columns stack on a narrow viewport the order still reads top to bottom.
    columnSets(): InviteColumn[][] {
      const half = Math.ceil(this.columns.length / 2);
      return [this.columns.slice(0, half), this.columns.slice(half)];
    },
    unmapped(): InviteColumn[] {
      return this.columns.filter(col => !this.selections[col.header]);
    },
    missingRequired(): InviteColumn[] {
      return this.unmapped.filter(col => col.required);
    },
    missingOptional(): InviteColumn[] {
      return this.unmapped.filter(col => !col.required);
    },
    // Field (canonical header) -> the CSV column keys taken by the OTHER fields, which its dropdown must not offer.
    takenByOthers(): Record<string, Set<string>> {
      const taken: Record<string, Set<string>> = {};
      for (const col of this.columns) {
        taken[col.header] = new Set(this.columns
          .filter(other => other.header !== col.header)
          .map(other => this.selections[other.header]?.key)
          .filter((key): key is string => key != null));
      }
      return taken;
    }
  },
  watch: {
    // A new file's headers start a fresh, auto-matched mapping so stale selections never leak between uploads.
    headers: {
      handler() {
        this.buildSelections();
      },
      immediate: true
    }
  },
  methods: {
    // The CSV columns a field's dropdown offers: every column not selected by another field (its own selection stays
    // available so it remains shown and can be cleared).
    availableHeaders(col: InviteColumn): CsvHeader[] {
      const taken = this.takenByOthers[col.header];
      return this.headers.filter(h => !taken.has(h.key));
    },
    buildSelections() {
      const next: Record<string, CsvHeader | null> = {};
      const used = new Set<string>();
      for (const col of this.columns) {
        const match = this.autoMatch(col, used);
        next[col.header] = match;
        if (match)
          used.add(match.key);
      }
      this.selections = next;
      this.validationAttempted = false;
    },
    // Best-effort pre-selection: match a client column whose camelCase key equals our field's key, or whose header
    // text equals ours ignoring case and non-alphanumerics, skipping columns an earlier field already took (a column
    // feeds only one field). Returns null when nothing matches.
    autoMatch(col: InviteColumn, used: Set<string>): CsvHeader | null {
      const candidates = this.headers.filter(h => !used.has(h.key));
      const byKey = candidates.find(h => h.key === col.key);
      if (byKey)
        return byKey;

      const target = this.normalize(col.header);
      return candidates.find(h => this.normalize(h.original) === target) ?? null;
    },
    normalize(value: string): string {
      return (value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    },
    highlightClass(col: InviteColumn): Record<string, boolean> {
      const unmapped = this.validationAttempted && !this.selections[col.header];
      return {
        mappingMissingRequired: unmapped && col.required,
        mappingMissingOptional: unmapped && !col.required
      };
    },
    validate() {
      this.validationAttempted = true;
      if (this.missingRequired.length > 0)
        return;

      const mapping: InviteColumnMapping = {};
      for (const col of this.columns) {
        const selected = this.selections[col.header];
        if (selected && selected.key)
          mapping[col.header] = selected.key;
      }
      this.$emit('validated', mapping);
    }
  }
});
</script>

<style lang="scss">
.inviteCsvMappingForm {
  margin-top: 8px;
  padding-left: $standard_space;
  border-left: 3px solid $very_light_gray;

  .mappingIntro {
    color: $faint_text;
    font-size: 0.9rem;
    margin-bottom: 12px;
  }

  // Two columns side by side; each needs ~380px, so once the form is too narrow for both, the second wraps below
  // the first.
  .mappingGrid {
    display: flex;
    flex-wrap: wrap;
    column-gap: $standard_space * 2;
    row-gap: 8px;
  }

  .mappingColumn {
    flex: 1 1 380px;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .mappingRow {
    display: grid;
    grid-template-columns: 170px minmax(0, 1fr);
    align-items: center;
    gap: 12px;

    .mappingFieldLabel {
      font-weight: $font_weight_bold;
      margin: 0;

      .req {
        color: $error_color;
        margin-left: 2px;
      }
    }

    &.mappingMissingRequired .multiselect__tags {
      background: #f9d7dd;
      border: 2px solid $error_color;
    }

    &.mappingMissingOptional .multiselect__tags {
      background: $pale_yellow;
      border: 2px solid $strong_yellow;
    }
  }

  .mappingSummary {
    margin-top: 10px;
  }

  .mappingSummaryWarning {
    color: $faint_text;
  }

  .mappingActions {
    display: flex;
    justify-content: flex-end;
    margin-top: 12px;
  }
}
</style>
